import {
  AccountApiError,
  CreditError,
  type AccountProvider,
  type AccountSession,
  type CreditBalance,
  type CreditChargeResult,
  type CreditQuote,
  type QuoteInput,
} from './types';

/**
 * 真实账号适配器（同源调用本站边缘接口）。
 *
 * 本站边缘层已经完成与账号中心的交互（BFF 换票 + 服务端积分代理），所以浏览器侧只需要：
 * - `GET /api/account/session` 读登录态（HttpOnly Cookie，credentials: 'include'）；
 * - `GET /api/account/credits` 读积分（由本站服务端向账号中心查询，前端不持有中心令牌）；
 * - `POST /api/auth/logout` 退出；
 * - 登录：整页跳转 `/api/auth/oidc/start`（账号中心完成登录与授权后自动回跳）。
 */

export interface HttpAccountProviderOptions {
  /** 留空表示同源。 */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** 注入导航函数（便于测试）；默认 window.location.assign */
  navigate?: (url: string) => void;
}

interface Envelope<T> {
  ok: boolean;
  data?: T;
  code?: string;
  message?: string;
  startUrl?: string;
}

const SIGN_IN_PATH = '/api/auth/oidc/start';

export class HttpAccountProvider implements AccountProvider {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly navigate: (url: string) => void;

  constructor(options: HttpAccountProviderOptions = {}) {
    this.baseUrl = (options.baseUrl ?? '').replace(/\/$/, '');
    this.fetchImpl = options.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
    this.navigate = options.navigate ?? ((url: string) => window.location.assign(url));
  }

  async getSession(): Promise<AccountSession> {
    return this.request<AccountSession>('GET', '/api/account/session');
  }

  async getCredits(): Promise<CreditBalance> {
    try {
      const data = await this.request<CreditBalance>('GET', '/api/account/credits');
      return {
        balance: data.balance,
        currency: 'credit',
        dailyPoints: data.dailyPoints ?? 0,
        bonusPoints: data.bonusPoints ?? 0,
        dailyGrantQuota: data.dailyGrantQuota ?? 0,
        day: data.day ?? '',
        note: data.note,
      };
    } catch (error) {
      throw mapCreditError(error, '读取积分失败');
    }
  }

  signIn(): void {
    this.navigate(`${this.baseUrl}${SIGN_IN_PATH}`);
  }

  async signOut(): Promise<void> {
    await this.request<{ loggedOut: boolean }>('POST', '/api/auth/logout', {});
  }

  async quoteCredits(input: QuoteInput): Promise<CreditQuote> {
    return this.request<CreditQuote>('POST', '/api/conversions/quote', {
      operation: input.operation,
      sourceSize: input.sourceSize,
      sourceFormat: input.sourceFormat,
      targetFormat: input.targetFormat,
      idempotencyKey: createIdempotencyKey('quote'),
    }).catch((error: unknown) => {
      throw mapCreditError(error, '云端报价暂不可用（云端转换尚未开放）');
    });
  }

  async chargeCredits(quote: CreditQuote): Promise<CreditChargeResult> {
    return this.request<CreditChargeResult>('POST', '/api/conversions/charge', {
      idempotencyKey: quote.idempotencyKey,
      cost: quote.cost,
      reasonCode: quote.reasonCode,
    }).catch((error: unknown) => {
      throw mapCreditError(error, '扣费请求失败（云端转换尚未开放）');
    });
  }

  async refundCredits(input: { transactionId: string; reason: string; idempotencyKey: string }): Promise<void> {
    await this.request<{ refunded: boolean }>('POST', '/api/conversions/refund', input).catch((error: unknown) => {
      throw mapCreditError(error, '退款请求失败');
    });
  }

  private async request<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    }).catch((error: unknown) => {
      throw new CreditError('NETWORK', error instanceof Error ? error.message : '网络请求失败');
    });

    const payload = (await response.json().catch(() => null)) as Envelope<T> | null;
    if (!response.ok || !payload || payload.ok !== true) {
      const code = payload?.code ?? `HTTP_${response.status}`;
      const message = payload?.message ?? `账号接口返回 ${response.status}`;
      throw new AccountApiError(code, message, response.status, payload?.startUrl);
    }
    return payload.data as T;
  }
}

function mapCreditError(error: unknown, fallback: string): CreditError {
  if (error instanceof CreditError) return error;
  if (error instanceof AccountApiError) {
    if (error.status === 401) return new CreditError('NOT_AUTHENTICATED', error.message || '请先登录 YoGo 账号');
    if (error.code === 'CREDITS_UNAVAILABLE') return new CreditError('UNAVAILABLE', error.message || fallback);
    if (error.code === 'NOT_FOUND') return new CreditError('NOT_IMPLEMENTED', fallback);
    if (error.code === 'CREDITS_INSUFFICIENT') return new CreditError('INSUFFICIENT_CREDITS', error.message);
    return new CreditError('UNKNOWN', error.message || fallback);
  }
  return new CreditError('UNKNOWN', error instanceof Error ? error.message : fallback);
}

export function createIdempotencyKey(prefix = 'idem'): string {
  const uuid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}_${uuid}`;
}

export function createAccountProvider(options: HttpAccountProviderOptions = {}): AccountProvider {
  return new HttpAccountProvider(options);
}
